import type { CustomerRank, CustomerRankStats } from '@shared/types';

export interface RankThreshold {
  rank: CustomerRank;
  minAmount: number; // 累計請求確定額の下限
  minOrders: number; // 累計受注件数の下限
}

/**
 * ランクしきい値（高い順）。金額・件数の両条件（AND）を満たす最上位ランクを採用する。
 * 境界値は将来のマスタ化を想定。ここを編集すれば運用上のしきい値を調整できる。
 */
export const RANK_THRESHOLDS: readonly RankThreshold[] = [
  { rank: 'SSS', minAmount: 100_000_000, minOrders: 1 },
  { rank: 'SS', minAmount: 50_000_000, minOrders: 1 },
  { rank: 'S', minAmount: 10_000_000, minOrders: 10 },
  { rank: 'A', minAmount: 5_000_000, minOrders: 3 },
  { rank: 'B', minAmount: 1_000_000, minOrders: 1 },
  { rank: 'C', minAmount: 0, minOrders: 0 },
];

/** 昇順（最下位→最上位）。段階的な降格計算に使う。 */
export const RANK_ORDER: readonly CustomerRank[] = ['C', 'B', 'A', 'S', 'SS', 'SSS'];

/** 新規顧客の初期ランク。 */
export const DEFAULT_RANK: CustomerRank = 'C';

/** ランクを1段階下げる（最下位 C で止まる）。 */
function oneStepDown(rank: CustomerRank): CustomerRank {
  const i = RANK_ORDER.indexOf(rank);
  return i <= 0 ? 'C' : RANK_ORDER[i - 1];
}

/**
 * 累計実績からランクを算定する。
 * - 昇格: 金額・件数の両条件を満たす最上位ランクを採用する（C は常に該当＝最下位）。
 * - 降格: 直近12ヶ月の請求が 0（1年間取引なし）の休眠は、算定ランクを1段階下げる（C まで）。
 */
export function computeCustomerRank(stats: CustomerRankStats): CustomerRank {
  const base =
    RANK_THRESHOLDS.find(
      (t) => stats.lifetimeInvoiced >= t.minAmount && stats.lifetimeOrderCount >= t.minOrders,
    )?.rank ?? DEFAULT_RANK;
  // 1年間取引なし（直近12ヶ月の請求が0）の休眠顧客は1段階降格
  return stats.rolling12mInvoiced <= 0 ? oneStepDown(base) : base;
}
